import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, Trash2, Mail, Phone } from "lucide-react";

type Status = "new" | "contacted" | "attended" | "declined";

interface VisitRequest {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  job_title: string;
  industry: string;
  phone: string | null;
  chapter: "washington" | "union" | "all";
  heard_about: string;
  status: Status;
  admin_notes: string | null;
  created_at: string;
}

const statusVariant: Record<Status, "default" | "secondary" | "outline" | "destructive"> = {
  new: "default",
  contacted: "secondary",
  attended: "outline",
  declined: "destructive",
};

const AdminVisitRequestList = () => {
  const [requests, setRequests] = useState<VisitRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | Status>("all");
  const [selected, setSelected] = useState<VisitRequest | null>(null);
  const [notesDraft, setNotesDraft] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("visit_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error(error.message);
    } else {
      setRequests((data || []) as VisitRequest[]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id: string, status: Status) => {
    const { error } = await supabase
      .from("visit_requests")
      .update({ status })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Status updated");
    setRequests((r) => r.map((x) => (x.id === id ? { ...x, status } : x)));
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const saveNotes = async () => {
    if (!selected) return;
    const { error } = await supabase
      .from("visit_requests")
      .update({ admin_notes: notesDraft })
      .eq("id", selected.id);
    if (error) return toast.error(error.message);
    toast.success("Notes saved");
    setRequests((r) =>
      r.map((x) => (x.id === selected.id ? { ...x, admin_notes: notesDraft } : x)),
    );
    setSelected({ ...selected, admin_notes: notesDraft });
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this visit request? This cannot be undone.")) return;
    const { error } = await supabase.from("visit_requests").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    setRequests((r) => r.filter((x) => x.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const visible = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle>Visit Requests</CardTitle>
            <CardDescription>
              People who submitted the "Visit a Meeting" form.
            </CardDescription>
          </div>
          <Select value={filter} onValueChange={(v) => setFilter(v as any)}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({requests.length})</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="attended">Attended</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : visible.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">No requests to show.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Chapter</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{r.first_name} {r.last_name}</div>
                      <div className="text-xs text-muted-foreground">{r.email}</div>
                    </TableCell>
                    <TableCell>
                      <div>{r.company}</div>
                      <div className="text-xs text-muted-foreground">{r.job_title}</div>
                    </TableCell>
                    <TableCell className="capitalize">{r.chapter}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant[r.status]} className="capitalize">
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelected(r);
                          setNotesDraft(r.admin_notes || "");
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.first_name} {selected.last_name}</DialogTitle>
                <DialogDescription>
                  Submitted {new Date(selected.created_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <Detail label="Email">
                    <a href={`mailto:${selected.email}`} className="text-primary inline-flex items-center gap-1 hover:underline">
                      <Mail className="h-3 w-3" /> {selected.email}
                    </a>
                  </Detail>
                  {selected.phone && (
                    <Detail label="Phone">
                      <a href={`tel:${selected.phone}`} className="text-primary inline-flex items-center gap-1 hover:underline">
                        <Phone className="h-3 w-3" /> {selected.phone}
                      </a>
                    </Detail>
                  )}
                  <Detail label="Company">{selected.company}</Detail>
                  <Detail label="Job title">{selected.job_title}</Detail>
                  <Detail label="Industry">{selected.industry}</Detail>
                  <Detail label="Chapter requested" className="capitalize">{selected.chapter}</Detail>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    How they heard about RCN
                  </Label>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{selected.heard_about}</p>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={selected.status}
                    onValueChange={(v) => updateStatus(selected.id, v as Status)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="attended">Attended</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Internal notes</Label>
                  <Textarea
                    rows={4}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Notes for the membership committee..."
                  />
                  <Button size="sm" onClick={saveNotes}>Save notes</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Detail = ({
  label, children, className,
}: { label: string; children: React.ReactNode; className?: string }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-0.5 ${className || ""}`}>{children}</div>
  </div>
);

export default AdminVisitRequestList;
